/**
 * Centralized Analytics Configuration
 *
 * This module provides a single source of truth for determining whether
 * analytics should be enabled based on environment variables.
 */

/**
 * Determines if analytics should be enabled based on environment variables
 *
 * Analytics is enabled only when:
 * - DEV_MODE is not "true" (production mode)
 * - VITE_APP_ANALYTICS_ENABLED is "true"
 *
 * @returns {boolean} True if analytics should be enabled
 */
export function isAnalyticsEnabled(): boolean {
	const devMode = import.meta.env.DEV_MODE as string;
	const analyticsEnabled = import.meta.env.VITE_APP_ANALYTICS_ENABLED;
	const analyticsAreEnabled = devMode !== "true" && analyticsEnabled === "true";

	return analyticsAreEnabled;
}

/**
 * Get analytics configuration object with computed enabled state
 * @returns {object} Configuration object with enabled state
 */
export function getAnalyticsConfig() {
	return {
		enabled: isAnalyticsEnabled(),
		devMode: import.meta.env.DEV_MODE,
		analyticsEnabled: import.meta.env.VITE_APP_ANALYTICS_ENABLED,
		environment: import.meta.env.VITE_APP_ENV,
	};
}

/**
 * React hook for components that need to conditionally render analytics-related UI
 *
 * @returns {object} Analytics configuration state
 */
export function useAnalyticsConfig() {
	return getAnalyticsConfig();
}
